import { useState } from 'react'
import { type FieldValues, type UseFormReturn } from 'react-hook-form'

export interface LinkBaseline {
  isStale: boolean
  isDraftStale: boolean
  capture: () => void
  captureDraft: () => void
}

// Capturing resets the form's pristine baseline to the just-persisted values, so dirty tracking
// answers "did the form change since these settings were last written" — including clearing again
// when an edit is reverted.
//
// Two questions share that one baseline. `isStale` asks whether the *client* is looking at outdated
// settings, so it only counts once something has actually been shared. `isDraftStale` asks whether
// the held draft still matches the form, which matters even when a share attempt failed — otherwise
// a cached draft from a failed copy would be reused after further edits.
export const useLinkBaseline = <TValues extends FieldValues>(form: UseFormReturn<TValues>): LinkBaseline => {
  const [linkShared, setLinkShared] = useState(false)

  const captureDraft = () => form.reset(form.getValues(), { keepErrors: true, keepTouched: true, keepIsValid: true })

  const capture = () => {
    captureDraft()
    setLinkShared(true)
  }

  const isDirty = Object.keys(form.formState.dirtyFields).length > 0

  return { isStale: linkShared && isDirty, isDraftStale: isDirty, capture, captureDraft }
}
